import type { RoundDetail } from "../api/types";

/**
 * Per-player state at an instant, derived from the round's step functions.
 *
 * Health, armour, ability charges, ultimate points, held item and credits all
 * arrive as changes carrying an absolute value, so the state at time t is
 * simply the last change at or before t. The index below sorts them once per
 * round; each lookup is a binary search.
 */
export interface Snapshot {
  health: number;
  alive: boolean;
  /** Points of armour bought this round, or zero once it has broken. */
  armour: number;
  /** Light, Heavy or Plasma, while the armour holds. */
  armourName: string;
  /** The item currently in hand, weapon or otherwise. */
  weapon: string;
  /** Every weapon held this round, most recently held last. */
  weapons: string[];
  credits: number | null;
  /** Charges left, by reference slot. */
  charges: Record<string, number>;
  ultimate: number;
}

interface Step<T> {
  timeMs: number;
  value: T;
}

interface PawnIndex {
  health: Step<{ value: number; alive: boolean }>[];
  armour: Step<{ amount: number; label: string }>[];
  weapon: Step<string>[];
  credits: Step<number>[];
  charges: Map<string, Step<number>[]>;
  ultimate: Step<number>[];
}

export type RoundIndex = Map<number, PawnIndex>;

export function buildIndex(detail: RoundDetail): RoundIndex {
  const index: RoundIndex = new Map();
  const forPawn = (pawn: number): PawnIndex => {
    let entry = index.get(pawn);
    if (!entry) {
      entry = {
        health: [],
        armour: [],
        weapon: [],
        credits: [],
        charges: new Map(),
        ultimate: [],
      };
      index.set(pawn, entry);
    }
    return entry;
  };

  for (const l of detail.life) {
    // Armour is not a damage section the game replicates a value for, so only
    // health comes from here.
    if (l.section !== "health") continue;
    forPawn(l.pawn).health.push({
      timeMs: l.timeMs,
      value: { value: l.value, alive: l.alive },
    });
  }
  for (const l of detail.loadouts) {
    const entry = forPawn(l.pawn);
    if (l.credits !== undefined && l.credits !== null) {
      entry.credits.push({ timeMs: l.timeMs, value: l.credits });
    } else if (l.weapon) {
      entry.weapon.push({ timeMs: l.timeMs, value: l.weapon });
    }
  }
  for (const a of detail.armament ?? []) {
    const entry = forPawn(a.pawn);
    if (a.kind === "armour") {
      entry.armour.push({
        timeMs: a.timeMs,
        value: { amount: a.amount, label: a.label ?? "" },
      });
    } else if (a.kind === "ultimate") {
      entry.ultimate.push({ timeMs: a.timeMs, value: a.amount });
    } else if (a.kind === "ability" && a.slot) {
      const steps = entry.charges.get(a.slot) ?? [];
      steps.push({ timeMs: a.timeMs, value: a.amount });
      entry.charges.set(a.slot, steps);
    }
  }

  for (const entry of index.values()) {
    entry.health.sort(byTime);
    entry.armour.sort(byTime);
    entry.weapon.sort(byTime);
    entry.credits.sort(byTime);
    entry.ultimate.sort(byTime);
    for (const steps of entry.charges.values()) steps.sort(byTime);
  }
  return index;
}

function byTime(a: { timeMs: number }, b: { timeMs: number }): number {
  return a.timeMs - b.timeMs;
}

/** Last step at or before timeMs, or undefined when the round has not reached
 *  its first one. */
function lastAt<T>(steps: Step<T>[], timeMs: number): T | undefined {
  let lo = 0;
  let hi = steps.length - 1;
  let found: T | undefined;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (steps[mid]!.timeMs <= timeMs) {
      found = steps[mid]!.value;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

const FULL_HEALTH = 100;

const EMPTY: Snapshot = {
  health: FULL_HEALTH,
  alive: true,
  armour: 0,
  armourName: "",
  weapon: "",
  weapons: [],
  credits: null,
  charges: {},
  ultimate: 0,
};

export function snapshot(index: RoundIndex, pawn: number, timeMs: number): Snapshot {
  const entry = index.get(pawn);
  if (!entry) return EMPTY;

  const health = lastAt(entry.health, timeMs);
  const armour = lastAt(entry.armour, timeMs);
  const charges: Record<string, number> = {};
  for (const [slot, steps] of entry.charges) {
    charges[slot] = lastAt(steps, timeMs) ?? 0;
  }

  return {
    // Before the first replicated change a player is simply at full health.
    health: health?.value ?? FULL_HEALTH,
    alive: health?.alive ?? true,
    armour: armour?.amount ?? 0,
    armourName: armour?.label ?? "",
    weapon: lastAt(entry.weapon, timeMs) ?? "",
    weapons: entry.weapon.filter((s) => s.timeMs <= timeMs).map((s) => s.value),
    credits: lastAt(entry.credits, timeMs) ?? null,
    charges,
    ultimate: lastAt(entry.ultimate, timeMs) ?? 0,
  };
}
